/**
 * THE ANSWER TO A WRITE THAT DID NOT HAPPEN — one component, every screen.
 *
 * The sibling of `<DenialNotice>`, `<CapacityRefusalNotice>` and `<RoleRefusalNotice>`, and it does
 * not replace any of them: it DELEGATES to them. A denial is still rendered by the denial component,
 * a ceiling refusal by the ceiling one, a capacity refusal by the capacity one. What this adds is the
 * routing — so that a screen performing a write has one line of markup instead of a five-branch
 * ternary that the next screen copies and shortens.
 *
 * That copying is the actual risk. The Owner's direction survives only while every screen keeps the
 * states apart, and a `catch { setFailed(true) }` in the twelfth screen erases it silently for that
 * screen. Here, the erasure would have to be done once, in a file whose whole subject is not doing it.
 *
 * ── WHAT THE CALLER STILL OWNS ──────────────────────────────────────────────────────────────────
 *
 * The CONFLICT branch. A conflict body means different things per route — an excess of capabilities,
 * a capacity limit, a set of missing configuration fields, a lifecycle transition — and only the
 * screen knows which route it called. So a screen passes `renderConflict`, which is where its own
 * mapper turns the body into Hebrew. There is a written fallback for a conflict a screen did not
 * anticipate, and it says the two things that are always true: the server refused, and it refused
 * before writing, so nothing changed.
 *
 * NOTHING HERE DECIDES PERMISSION. It renders a decision that arrived.
 */

import type { ReactNode } from 'react';
import type { ActionFailure } from '../api/actionOutcome';
import { NOT_FOUND_HE, UNREACHABLE_HE } from '../api/actionOutcome';
import { stepUpTextFor } from '../api/stepUpText';
import { DenialNotice } from './DenialNotice';
import { ErrorState, StateBlock } from './StateBlock';
import { routes } from '../routes';

/** Where each step-up sends the person. Two of the three are two DIFFERENT screens (see stepUpText). */
const STEP_UP_HREF: Readonly<Record<ReturnType<typeof stepUpTextFor>['action'], string>> = {
  sign_in_again: routes.signIn,
  challenge: routes.mfaChallenge,
  enrolment: routes.mfaEnrolment,
};

export interface ActionFailureNoticeProps {
  readonly failure: ActionFailure;
  /**
   * The screen's own reading of a conflict body. Returning `null` falls through to the written
   * generic — which is honest rather than silent, and is what a route this screen has not taught the
   * mapper about produces.
   */
  readonly renderConflict?: (failure: Extract<ActionFailure, { kind: 'conflict' }>) => ReactNode;
}

export function ActionFailureNotice({
  failure,
  renderConflict,
}: ActionFailureNoticeProps): JSX.Element {
  if (failure.kind === 'denied') {
    // The gate's own body, through the gate's own component. Uniform outside the tenant.
    return <DenialNotice deny={failure.body} />;
  }

  if (failure.kind === 'stepUp') {
    if (failure.need === null) {
      // The server required a step-up and named a challenge this build has no words for. Saying less
      // beats naming the wrong screen — a person sent to enrol when they hold a working authenticator
      // ends up with a second enrolled device.
      return (
        <StateBlock
          titleHe="נדרש אימות נוסף לפעולה הזו"
          bodyHe="השרת דורש אימות נוסף לפני הפעולה, והפעולה לא בוצעה."
          tone="error"
          nextStep={<a className="btn btn--primary" href={routes.personalSecurity}>מסך האבטחה שלי</a>}
        />
      );
    }
    const text = stepUpTextFor(failure.need);
    return (
      <StateBlock
        titleHe={text.titleHe}
        bodyHe={text.explanationHe}
        tone={text.tone === 'warn' ? 'unavailable' : 'error'}
        nextStep={
          <a className="btn btn--primary" href={STEP_UP_HREF[text.action]}>
            {text.nextStepHe}
          </a>
        }
      />
    );
  }

  if (failure.kind === 'conflict') {
    const rendered = renderConflict?.(failure);
    if (rendered !== undefined && rendered !== null) return <>{rendered}</>;
    return (
      <StateBlock
        titleHe="הפעולה לא בוצעה"
        // Both halves are true whichever conflict it was, and both are worth more than "אירעה תקלה".
        bodyHe="השרת סירב לבקשה. הסירוב נעשה לפני הכתיבה, ולכן לא נשמר שום שינוי."
        tone="error"
        nextStep={<p>אפשר לרענן את המסך ולראות את המצב העדכני לפני ניסיון נוסף.</p>}
      />
    );
  }

  if (failure.kind === 'notFound') {
    return (
      <StateBlock
        titleHe={NOT_FOUND_HE.titleHe}
        bodyHe={NOT_FOUND_HE.bodyHe}
        tone="unavailable"
        nextStep={<p>{NOT_FOUND_HE.nextStepHe}</p>}
      />
    );
  }

  if (failure.kind === 'unreachable') {
    return (
      <StateBlock
        titleHe={UNREACHABLE_HE.titleHe}
        bodyHe={UNREACHABLE_HE.bodyHe}
        tone="error"
        nextStep={<p>{UNREACHABLE_HE.nextStepHe}</p>}
      />
    );
  }

  // 09 §5.8 / §9.1 V3: a short reference code, and never the exception text.
  return <ErrorState requestUid={failure.requestUid} />;
}
